<template>
  <div ref="uploader" data-testid="customer-import-uploader"></div>
</template>

<script>
import Uppy from '@uppy/core'
import FileInput from '@uppy/file-input'
import firebase from 'firebase/app'
import 'firebase/storage'
import { parseCustomerImportCsv } from '@/lib/customer-import-csv'

// @group Customer Import
export default {
  name: 'FileUploader',
  props: {
    // The text to render in the upload button
    buttonText: {
      type: String,
      required: true
    },
    // The style for the upload button
    buttonClass: {
      type: String,
      required: true
    }
  },
  watch: {
    buttonText() {
      this.$refs.uploader.querySelector('button').innerText = this.buttonText
    }
  },
  data: () => ({
    modalTitle: 'TCPA Compliance Agreement',
    modalMessage: `
        By pressing "Agree" you confirm that the names and/or phone numbers that you are uploading
        are compliant with respect to all applicable telemarketing rules and laws. If this list is 
        to be used for marketing purposes, you confirm that you have express written consent for 
        all recipients to send any message.
        `
  }),
  methods: {
    // @vuese
    // Parses the selected file locally, checks the live updates channel, then uploads it to
    // Firebase and emits the URL with the parsed rows
    // @arg file: the selected file, RETURNS: none
    async uploadFile(file) {
      const button = this.$refs.uploader.querySelector('button')
      button.classList.add('is-loading')
      button.disabled = true

      try {
        let rows
        try {
          rows = await parseCustomerImportCsv(file.data)
        } catch (error) {
          // Emitted when the file has no rows or cannot be read; nothing is uploaded
          this.$emit('parse-error')
          return
        }

        const ready = await this.$store.dispatch('websocketV2/awaitUserChannelReady')
        if (!ready) {
          // Emitted when the live updates channel is not subscribed; nothing is uploaded
          this.$emit('realtime-error')
          return
        }

        const randomString = Math.random().toString(36).substring(7)
        const fileExtension = file.name.split('.').pop()
        const storageRef = firebase
          .storage()
          .ref()
          .child(`uploads/${randomString}.${fileExtension}`)

        const snap = await storageRef.put(file.data)
        const url = await snap.ref.getDownloadURL()

        // Notifies parent that a file was uploaded
        // @arg { url, rows }: the URL of the file in Firebase and the locally parsed rows
        this.$emit('uploaded', { url, rows })
      } finally {
        button.classList.remove('is-loading')
        button.disabled = false
      }
    }
  },
  mounted() {
    const uppy = new Uppy({
      restrictions: {
        allowedFileTypes: ['image/*', 'text/csv']
      },
      onBeforeFileAdded: (file) => {
        const h = this.$createElement
        const titleVNode = h('div', [
          h('h2', { class: ['mb-0'], domProps: { innerHTML: `${this.modalTitle}` } })
        ])
        this.$bvModal
          .msgBoxConfirm(this.modalMessage, {
            id: 'customer-import-tcpa-modal',
            title: [titleVNode],
            okTitle: 'AGREE',
            cancelTitle: 'DISAGREE',
            cancelVariant: 'danger',
            footerClass: 'p-4',
            centered: true
          })
          .then((value) => {
            if (value) this.uploadFile(file)
            else return
          })
          .catch((err) => {
            console.log(err)
          })
      }
    })

    uppy.use(FileInput, {
      target: this.$refs.uploader,
      locale: {
        strings: {
          chooseFiles: this.buttonText
        }
      }
    })

    this.$refs.uploader
      .querySelector('button')
      .classList.add('btn', this.buttonClass || 'btn-white')
  }
}
</script>

<style scoped lang="scss">
::v-deep .btn-white {
  border-color: #d2ddec;
}
</style>
